import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  Grid,
  GridColumn,
  type GridCellProps,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import {
  Notification,
  NotificationGroup,
} from "@progress/kendo-react-notification";
import {
  useActOnLoadFailedDeal,
  useLoadFailedDeals,
  type LoadFailedDealAction,
  type LoadFailedDealRow,
} from "src/dm/hooks/useLoadFailedDeals";

// Legacy Admin/ManageLoadFailedDeals.aspx — lists deals stuck in LOADFAILED
// and exposes per-row Approve / Review actions. Each action POSTs to
// /admin/load-failed-deals/{dealId}/{action}; the API dispatches through
// WorkflowService so signature + notification side-effects still fire.

const cardStyle: React.CSSProperties = {
  background: "#fff",
  padding: "1rem 1.25rem",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const ManageLoadFailedDeals = () => {
  const list = useLoadFailedDeals();
  const act = useActOnLoadFailedDeal();
  const [pendingAction, setPendingAction] = useState<{
    dealId: number;
    action: LoadFailedDealAction;
  } | null>(null);
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    text: string;
  } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.type === "error" ? 6000 : 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleAction = async (dealId: number, action: LoadFailedDealAction) => {
    setPendingAction({ dealId, action });
    setToast(null);
    try {
      const result = await act.mutateAsync({ dealId, action });
      setToast({
        type: "success",
        text: `Deal ${result.dealId} → ${result.appliedStatus ?? action.toUpperCase()}`,
      });
    } catch (err: unknown) {
      const r = (err as { response?: { status?: number; data?: { message?: string; reason?: string } } }).response;
      const msg =
        r?.data?.reason ??
        r?.data?.message ??
        (err instanceof Error ? err.message : "unknown error");
      setToast({
        type: "error",
        text: `Deal ${dealId} ${action} failed: ${msg}`,
      });
    } finally {
      setPendingAction(null);
    }
  };

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as LoadFailedDealRow;
    const isThisRowPending =
      pendingAction?.dealId === row.dealId;
    return (
      <td>
        <Button
          size="small"
          themeColor="primary"
          disabled={act.isPending}
          onClick={() => handleAction(row.dealId, "approve")}
          style={{ marginRight: "0.5rem" }}
        >
          {isThisRowPending && pendingAction?.action === "approve"
            ? "…"
            : "Approve"}
        </Button>
        <Button
          size="small"
          disabled={act.isPending}
          onClick={() => handleAction(row.dealId, "review")}
        >
          {isThisRowPending && pendingAction?.action === "review"
            ? "…"
            : "Review"}
        </Button>
      </td>
    );
  };

  const DealIdCell = (props: GridCellProps) => {
    const row = props.dataItem as LoadFailedDealRow;
    return (
      <td>
        <Link to={`/dm/deals/${row.dealId}`} style={{ color: "#0a3d62" }}>
          {row.dealId}
        </Link>
      </td>
    );
  };

  const StatusDateCell = (props: GridCellProps) => {
    const row = props.dataItem as LoadFailedDealRow;
    return <td>{formatDate(row.statusDate)}</td>;
  };

  const rows = list.data ?? [];

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Link
        to="/dm/admin"
        style={{ fontSize: "0.85rem", color: "#0a3d62", textDecoration: "none" }}
      >
        ← Admin
      </Link>
      <div>
        <h1 style={{ margin: 0 }}>Re-run Failed Deal Loads</h1>
        <p style={{ color: "#475569", marginTop: "0.25rem" }}>
          Deals whose ERP outbound load failed land in <code>LOADFAILED</code>.
          Approve to push them to <code>APPROVE</code>; Review to send them
          back through the review queue. Actions route through the workflow
          service so signatures + notifications stay in sync.
        </p>
      </div>

      <section style={cardStyle}>
        {list.isLoading && <p>Loading failed deals…</p>}
        {list.isError && (
          <p style={{ color: "#b91c1c" }}>
            Failed to load deals:{" "}
            {list.error instanceof Error ? list.error.message : "unknown"}
          </p>
        )}
        {!list.isLoading && !list.isError && rows.length === 0 && (
          <p style={{ color: "#475569", margin: 0 }}>
            No deals are currently in <code>LOADFAILED</code> status.
          </p>
        )}
        {!list.isLoading && rows.length > 0 && (
          <Grid data={rows} sortable={false} resizable>
            <GridColumn
              field="dealId"
              title="Deal ID"
              width="110px"
              cells={{ data: DealIdCell }}
            />
            <GridColumn field="description" title="Description" />
            <GridColumn
              field="status"
              title="Current Status"
              width="160px"
            />
            <GridColumn
              field="statusDate"
              title="Status Date"
              width="180px"
              cells={{ data: StatusDateCell }}
            />
            <GridColumn field="ownerUserId" title="Owner" width="180px" />
            <GridColumn
              title=""
              width="200px"
              cells={{ data: ActionsCell }}
            />
          </Grid>
        )}
      </section>

      <NotificationGroup
        style={{
          position: "fixed",
          right: "1rem",
          bottom: "1rem",
          zIndex: 10000,
        }}
      >
        {toast && (
          <Notification
            type={{ style: toast.type, icon: true }}
            closable
            onClose={() => setToast(null)}
          >
            <span>{toast.text}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
};

export default ManageLoadFailedDeals;
